import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { DevEnvironmentSheet } from "@/components/DevEnvironmentSheet";
import { devToolsEnabled } from "@/lib/build-channel";
import { colors, fonts, gradients, shadows, spacing } from "@/lib/theme";

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

const TAP_THRESHOLD = 5;
const TAP_WINDOW_MS = 3000;

export default function WelcomeScreen() {
    const router = useRouter();
    // The environment switcher exists only in dev sessions and preview /
    // development builds. In the production app the logo is just a logo: the
    // switcher could point a nurse's login at any server (see lib/build-channel.ts).
    const [envSwitcherAvailable] = useState(devToolsEnabled);
    const [envSheetVisible, setEnvSheetVisible] = useState(false);
    const tapCountRef = useRef(0);
    const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleLogoTap = useCallback(() => {
        if (!envSwitcherAvailable) {
            return;
        }
        tapCountRef.current += 1;
        if (tapTimerRef.current) {
            clearTimeout(tapTimerRef.current);
        }
        if (tapCountRef.current >= TAP_THRESHOLD) {
            tapCountRef.current = 0;
            setEnvSheetVisible(true);
        } else {
            tapTimerRef.current = setTimeout(() => {
                tapCountRef.current = 0;
            }, TAP_WINDOW_MS);
        }
    }, [envSwitcherAvailable]);

    return (
        <View style={styles.container}>
            {/* Subtle gradient overlay at top */}
            <LinearGradient
                colors={[...gradients.surface]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
            />

            <SafeAreaView style={styles.safe}>
                {/* Centered content area */}
                <View style={styles.content}>
                    {/* Logo + Brand: tap 5x to open the env switcher (non-production builds only) */}
                    <TouchableOpacity
                        activeOpacity={1}
                        onPress={handleLogoTap}
                        style={styles.logoArea}
                    >
                        <PinwheelLogo size={81} />
                        <Text style={styles.brandName}>TaskHealth</Text>
                        <Text style={styles.brandSub}>RN Field App</Text>
                    </TouchableOpacity>

                    {/* Spacer */}
                    <View style={styles.spacer} />

                    {/* Tagline */}
                    <View style={styles.taglineArea}>
                        <Text style={styles.taglineText}>
                            {"Healthcare documentation,\nredesigned for nurses"}
                        </Text>
                        <Text style={styles.subtitleText}>
                            {"AI-powered forms, real-time scheduling,\nand seamless compliance"}
                        </Text>
                    </View>
                </View>

                {/* Bottom CTA area */}
                <View style={styles.ctaArea}>
                    <TouchableOpacity
                        activeOpacity={0.8}
                        onPress={() => router.push("/(auth)/login")}
                        style={styles.ctaButtonWrapper}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.ctaButton}
                        >
                            <Text style={styles.ctaText}>Get Started</Text>
                        </LinearGradient>
                    </TouchableOpacity>

                    <TouchableOpacity
                        onPress={() => router.push("/(auth)/login")}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.signInText}>
                            Already have an account? <Text style={styles.signInLink}>Sign in</Text>
                        </Text>
                    </TouchableOpacity>
                </View>
            </SafeAreaView>

            {envSwitcherAvailable ? (
                <DevEnvironmentSheet
                    visible={envSheetVisible}
                    onClose={() => setEnvSheetVisible(false)}
                />
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    safe: {
        flex: 1
    },
    content: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: spacing.xl
    },
    logoArea: {
        alignItems: "center"
    },
    brandName: {
        fontSize: 24,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        marginTop: spacing.base,
        letterSpacing: -0.3
    },
    brandSub: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        marginTop: spacing.xs,
        letterSpacing: 0.5
    },
    spacer: {
        height: spacing["3xl"]
    },
    taglineArea: {
        alignItems: "center"
    },
    taglineText: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        textAlign: "center",
        lineHeight: 26
    },
    subtitleText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 22,
        marginTop: spacing.md
    },
    ctaArea: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing["2xl"],
        alignItems: "center"
    },
    ctaButtonWrapper: {
        width: "100%"
    },
    ctaButton: {
        paddingVertical: 16,
        borderRadius: 20,
        alignItems: "center",
        ...shadows.brand
    },
    ctaText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    signInText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.base
    },
    signInLink: {
        color: colors.teal400,
        fontFamily: fonts.semiBold
    }
});
